import { useResetOnClose } from "@/lib/useResetOnClose";
import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { PageStatCards } from "@/components/PageStatCards";
import { TableToolbar } from "@/components/TableToolbar";
import { RowActions } from "@/components/RowActions";
import { confirmAction } from "@/lib/dialogs";
import { DetailDrawer, DrawerSection, DrawerField } from "@/components/DetailDrawer";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { confirmDuplicate, getErrorMessage, getFieldErrors } from "@/lib/errors";

export default function Clients() {
  const { isOrgAdmin } = useAuth();
  const { can } = usePermissions();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const canEdit = isOrgAdmin || can("CLIENTS", "edit");
  const canDelete = isOrgAdmin || can("CLIENTS", "delete");
  const remove = async (c) => {
    if (!(await confirmAction({ title: `Delete the client ${c.name}?`, message: "This works only for a client with no projects, bills or payments.", confirmLabel: "Delete" }))) return;
    try { await api.delete(`/revenue/clients/${c.id}`); toast.success(`${c.name} deleted`); setClients((prev) => prev.filter((x) => x.id !== c.id)); }
    catch (err) { toast.error(getErrorMessage(err)); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/revenue/clients");
      setClients(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (c) => {
    try {
      await api.patch(`/revenue/clients/${c.id}`, { is_active: !c.is_active });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const shown = useMemo(() => {
    if (!search) return clients;
    const q = search.toLowerCase();
    return clients.filter((c) =>
      (c.code || "").toLowerCase().includes(q) ||
      (c.name || "").toLowerCase().includes(q) ||
      (c.gstin || "").toLowerCase().includes(q) ||
      (c.contact_person || "").toLowerCase().includes(q)
    );
  }, [clients, search]);

  return (
    <div className="space-y-6" data-testid="clients-page">
      <PageHeader
        title="Clients"
        description="Client master data used for RA bill generation and receivables."
        action={(isOrgAdmin || can("CLIENTS", "create")) && <CreateClientDialog onCreated={load} />}
      />
      <PageStatCards loading={loading} stats={[
        { label: "Total Clients", value: clients.length, color: "blue" },
        { label: "Active", value: clients.filter((c) => c.is_active).length, color: "green" },
        { label: "Inactive", value: clients.filter((c) => !c.is_active).length, color: "gray" },
        { label: "With GSTIN", value: clients.filter((c) => c.gstin).length, color: "orange" },
      ]} />
      <TableToolbar
        search={search}
        onSearch={setSearch}
        csvData={shown.map((c) => ({
          Code: c.code, Name: c.name, GSTIN: c.gstin || "",
          Contact: c.contact_person || "", Phone: c.phone || "",
          "Retention %": c.default_retention_pct, "TDS %": c.default_tds_pct,
          Status: c.is_active ? "Active" : "Inactive",
        }))}
        csvFilename="clients.csv"
      />
      <div className="rounded-xl border border-border bg-card">
        {!loading && clients.length === 0 ? (
          <EmptyState title="No clients yet" testId="clients-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>GSTIN</TableHead>
                <TableHead>Retention %</TableHead>
                <TableHead>TDS %</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((c) => (
                <TableRow key={c.id} data-testid={`client-row-${c.code}`}>
                  <TableCell className="font-mono text-xs text-foreground">{c.code}</TableCell>
                  <TableCell className="text-foreground">{c.name}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{c.gstin || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{c.default_retention_pct}%</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{c.default_tds_pct}%</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={c.is_active} disabled={!isOrgAdmin} onCheckedChange={() => toggleActive(c)} data-testid={`client-active-toggle-${c.code}`} />
                      <Badge variant={c.is_active ? "default" : "secondary"}>{c.is_active ? "Active" : "Inactive"}</Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <RowActions testId={`client-actions-${c.code}`} onView={() => setSelected(c)} onEdit={canEdit ? () => setEditing(c) : undefined}
                      moreItems={canDelete ? [{ key: "delete", label: "Delete client", icon: Trash2, danger: true, onClick: () => remove(c), testId: `client-delete-${c.code}` }] : []} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <DetailDrawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.name}
        subtitle={`Client · ${selected?.code}`}
      >
        <DrawerSection label="Client Details">
          <DrawerField label="Code" value={selected?.code} mono />
          <DrawerField label="Name" value={selected?.name} />
          <DrawerField label="GSTIN" value={selected?.gstin} mono />
          <DrawerField label="Status" value={selected?.is_active ? "Active" : "Inactive"} />
        </DrawerSection>
        <DrawerSection label="Contact">
          <DrawerField label="Contact Person" value={selected?.contact_person} />
          <DrawerField label="Phone" value={selected?.phone} />
          <DrawerField label="Email" value={selected?.email} />
          <DrawerField label="Billing Address" value={selected?.billing_address} />
        </DrawerSection>
        <DrawerSection label="Rates">
          <DrawerField label="Default Retention %" value={selected?.default_retention_pct != null ? `${selected.default_retention_pct}%` : null} />
          <DrawerField label="Default TDS %" value={selected?.default_tds_pct != null ? `${selected.default_tds_pct}%` : null} />
        </DrawerSection>
        <DrawerSection label="Credit terms">
          {selected && <ClientCreditDays key={selected.id} client={selected} canEdit={can("CLIENTS", "edit")} onSaved={(days) => { setSelected({ ...selected, credit_days: days }); load(); }} />}
        </DrawerSection>
      </DetailDrawer>
      {editing && <EditClientDialog client={editing} onClose={() => setEditing(null)} onSaved={(saved) => { setEditing(null); setClients((prev) => prev.map((x) => (x.id === saved.id ? saved : x))); }} />}
    </div>
  );
}

export function ClientCreditDays({ client, canEdit, onSaved }) {
  const [days, setDays] = useState(String(client.credit_days ?? 30));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/revenue/clients/${client.id}`, { credit_days: Number(days) }); toast.success("Credit days saved. They apply to new bills."); onSaved(Number(days)); }
    catch (err) { toast.error(getErrorMessage(err)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-2" data-testid="client-credit-days">
      <DrawerField label="Credit days" value={`${client.credit_days ?? 30} days`} />
      {canEdit && <div className="flex items-end gap-2"><Input type="number" min="0" max="365" value={days} onChange={(e) => setDays(e.target.value)} className="w-28" data-testid="client-credit-days-edit" />
        <Button size="sm" variant="outline" disabled={saving || days === "" || Number(days) === Number(client.credit_days ?? 30)} onClick={save} data-testid="client-credit-days-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></div>}
    </div>
  );
}

function CreateClientDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", gstin: "", billing_address: "", contact_person: "", phone: "", email: "", default_retention_pct: "0", default_tds_pct: "0", credit_days: "30" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setForm({ code: "", name: "", gstin: "", billing_address: "", contact_person: "", phone: "", email: "", default_retention_pct: "0", default_tds_pct: "0", credit_days: "30" }); });      // closed without saving: opens fresh next time
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      const create = (allow) => api.post("/revenue/clients", form, allow ? { params: { allow_duplicate_name: true } } : undefined);
      try { await create(false); } catch (err) { if (await confirmDuplicate(err)) await create(true); else throw err; }
      toast.success(`Client ${form.code} created`);
      setForm({ code: "", name: "", gstin: "", billing_address: "", contact_person: "", phone: "", email: "", default_retention_pct: "0", default_tds_pct: "0", credit_days: "30" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-client-button"><Plus className="h-4 w-4" /> New Client</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Client</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-client-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input placeholder="Automatic if blank" data-testid="client-code-input" value={form.code} onChange={set("code")} className="mt-1.5" /><FieldError error={errors.code} /></div>
            <div><Label>Name</Label><Input required data-testid="client-name-input" value={form.name} onChange={set("name")} className="mt-1.5" /><FieldError error={errors.name} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>GSTIN</Label><Input data-testid="client-gstin-input" value={form.gstin} onChange={set("gstin")} className="mt-1.5" /></div>
            <div><Label>Contact person</Label><Input data-testid="client-contact-input" value={form.contact_person} onChange={set("contact_person")} className="mt-1.5" /></div>
          </div>
          <div><Label>Billing address</Label><Input data-testid="client-address-input" value={form.billing_address} onChange={set("billing_address")} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Phone</Label><Input data-testid="client-phone-input" value={form.phone} onChange={set("phone")} className="mt-1.5" /></div>
            <div><Label>Email</Label><Input data-testid="client-email-input" value={form.email} onChange={set("email")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Default retention %</Label><Input type="number" step="0.01" data-testid="client-retention-input" value={form.default_retention_pct} onChange={set("default_retention_pct")} className="mt-1.5" /></div>
            <div><Label>Default TDS %</Label><Input type="number" step="0.01" data-testid="client-tds-input" value={form.default_tds_pct} onChange={set("default_tds_pct")} className="mt-1.5" /></div>
          </div>
          <div><Label>Credit days</Label><Input type="number" min="0" max="365" data-testid="client-credit-days-input" value={form.credit_days} onChange={set("credit_days")} className="mt-1.5" />
            <p className="mt-1 text-[11px] text-muted-foreground">How many days after the bill date this client should pay. A bill's due date is worked out from it.</p></div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-client-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


/** Edit a client. The code stays as it is: bills, payments and reports refer to it. */
function EditClientDialog({ client, onClose, onSaved }) {
  const pick = (c) => ({ name: c.name || "", gstin: c.gstin || "", billing_address: c.billing_address || "", contact_person: c.contact_person || "", phone: c.phone || "",
    email: c.email || "", default_retention_pct: String(c.default_retention_pct ?? "0"), default_tds_pct: String(c.default_tds_pct ?? "0"), credit_days: String(c.credit_days ?? "30") });
  const [form, setForm] = useState(pick(client));
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...form, gstin: form.gstin.trim() || null, email: form.email.trim() || null, credit_days: Number(form.credit_days || 0) };
    try {
      const send = (allow) => api.patch(`/revenue/clients/${client.id}`, body, allow ? { params: { allow_duplicate_name: true } } : undefined);
      let res;
      try { res = await send(false); } catch (err) { if (await confirmDuplicate(err)) res = await send(true); else throw err; }
      toast.success(`${body.name} saved`);
      onSaved(res.data);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-client-dialog">
        <DialogHeader><DialogTitle>Edit client {client.code}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input value={client.code} disabled className="mt-1.5" data-testid="edit-client-code" /></div>
            <div><Label>Name</Label><Input required value={form.name} onChange={set("name")} className="mt-1.5" data-testid="edit-client-name" /></div>
            <div><Label>GSTIN</Label><Input value={form.gstin} onChange={set("gstin")} className="mt-1.5" data-testid="edit-client-gstin" /></div>
            <div><Label>Contact person</Label><Input value={form.contact_person} onChange={set("contact_person")} className="mt-1.5" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} className="mt-1.5" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={set("email")} className="mt-1.5" data-testid="edit-client-email" /></div>
          </div>
          <div><Label>Billing address</Label><Input value={form.billing_address} onChange={set("billing_address")} className="mt-1.5" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Retention %</Label><Input type="number" step="0.01" min="0" value={form.default_retention_pct} onChange={set("default_retention_pct")} className="mt-1.5" /></div>
            <div><Label>TDS %</Label><Input type="number" step="0.01" min="0" value={form.default_tds_pct} onChange={set("default_tds_pct")} className="mt-1.5" /></div>
            <div><Label>Credit days</Label><Input type="number" step="1" min="0" max="365" value={form.credit_days} onChange={set("credit_days")} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving || !form.name.trim()} data-testid="edit-client-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
